/* eslint-disable max-len */
import { useEffect } from 'react';
import proptypes from 'prop-types';
import {
  arrayOf as arrayType,
  bool as boolType,
  number as numberType,
  params,
  shape as shapeType,
  string as stringType,
  useDataModel
} from '@thd-nucleus/data-sources';
import {
  useHelmet, TitleTag, MetaTag, ScriptTag, LinkTag
} from '@thd-nucleus/thd-helmet';
import { copy } from '@thd-olt-functional/utils';
import {
  formatProductLabel,
  getDynamicOGTags,
  getHoistedImgPath,
  getPrimaryImage,
  getSecondaryImgPath
} from '../../helpers/product-helpers';
import {
  getVideoStructuredData,
  getBreadcrumbStructuredData,
  getProductStructuredData
} from '../structured-data';
import { DEFAULT_REVIEWS_SORT_BY } from '../../constants';
import {
  generatePprDescription,
  generatePprEncodedPath,
  generatePprTitle,
  getAggregateReviews,
  transformBrandName
} from "./ppr-metadata-helpers.js";

const { bool, string } = proptypes;

const pprMetaDataHandler = ({ data, props = {} }) => {
  if (!data?.product) {
    return [];
  }

  const {
    itemId,
    details,
    identifiers,
    seo,
    availabilityType,
    media,
    taxonomy,
    pricing,
    specificationGroup
  } = data?.product || {};

  const transformedBrandName = transformBrandName(identifiers?.brandName);
  const productDescription = (`${transformedBrandName} ${identifiers?.productLabel}`);

  const seoPageNumber = parseInt(props?.pageNumber, 10);

  const productLabel = formatProductLabel({ identifiers });
  const brandName = (!identifiers?.brandName || identifiers?.brandName?.toLowerCase() === 'unbranded') ? '' : identifiers.brandName;

  const title = generatePprTitle({
    brandName,
    identifiers,
    props,
    seoPageNumber,
    productDescription
  });

  const { seoDescription } = seo || {};
  const description = generatePprDescription({
    seoDescription,
    details,
    identifiers,
    props,
    seoPageNumber,
    productDescription
  });

  const encodedPath = generatePprEncodedPath({ identifiers, props });

  const canonical = `https://www.homedepot.com${encodedPath}`;
  const { video } = media || {};
  const primaryImage = getPrimaryImage({ media });
  const alternate = `android-app://com.thehomedepot/homedepot/product/${itemId}`;
  const hoistedImgPath = getHoistedImgPath({ primaryImage });
  const secondaryImgPath = getSecondaryImgPath({ media });
  const MAX_PAGE_NUMBER_TO_INDEX = 5;

  const dynamicOGTags = getDynamicOGTags({
    props, brandName, identifiers, title, description
  });

  const tags = [
    new TitleTag(title),
    new MetaTag({ name: 'description', content: description, id: 'description' }),
    ...dynamicOGTags,
    new MetaTag({ property: 'og:url', content: canonical, id: 'ogUrl' }),
    new MetaTag({ property: 'og:image', content: primaryImage, id: 'ogImage' }),
    new MetaTag({ property: 'og:site_name', content: 'The Home Depot', id: 'ogSiteName' }),
    new MetaTag({ property: 'fb:admins', content: '100005416960152', id: 'fbAdmins' }),
    new LinkTag({ rel: 'canonical', href: canonical, id: 'canonical' }),
    new LinkTag({ rel: 'alternate', href: alternate, id: 'alternate' }),
    new LinkTag({
      rel: 'preload', href: hoistedImgPath, id: 'preloadImg', as: 'image', fetchpriority: 'high'
    }),
  ];
  if (secondaryImgPath) {
    tags.push(new LinkTag({
      rel: 'preload', href: secondaryImgPath, id: 'preloadSecondaryImg', as: 'image', fetchpriority: 'high'
    }));
  }

  const aggregateReviews = getAggregateReviews(data);

  const productStructure = getProductStructuredData({
    aggregateReviews,
    reviews: data?.reviews?.Results,
    identifiers: {
      ...identifiers,
      productLabel
    },
    media,
    details,
    pricing,
    specifications: specificationGroup,
    availabilityType,
    ppq: props.ppq
  });

  tags.push(new ScriptTag({ content: productStructure, id: 'productStructureData' }));

  const breadcrumbStructuredData = getBreadcrumbStructuredData({
    canonical,
    breadcrumbs: copy(taxonomy?.breadCrumbs || []).data,
    title,
    brandName,
    brandLinkUrl: taxonomy?.brandLinkUrl
  });

  tags.push(new ScriptTag({ content: breadcrumbStructuredData, id: 'breadcrumbStructureData' }));

  if (availabilityType?.discontinued || props?.pageNumber > MAX_PAGE_NUMBER_TO_INDEX) {
    tags.push(new MetaTag({ name: 'ROBOTS', content: 'NOINDEX, NOFOLLOW', id: 'robots' }));
  } else {
    tags.push(new MetaTag({ name: 'robots', content: 'max-image-preview:large', id: 'robots' }));
  }

  if (video?.length) {
    const videoStructuredData = getVideoStructuredData({
      videos: video,
      productLabel,
    });
    tags.push(new ScriptTag({ content: videoStructuredData, id: 'videoStructureData' }));
  }

  return tags;
};

const PprMetadata = ({
  itemId, page = '1', ppq = false
}) => {
  const pageSize = 30;

  useEffect(() => { LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('product-metadata.ready'); }, []);
  const productOptions = {
    variables: {
      itemId
    }
  };

  const pageNumber = page || '1';
  const startIndex = pageNumber === '1' ? 1 : (pageNumber - 1) * pageSize + 1;

  const reviewsOptions = {
    variables: {
      itemId,
      ...(pageNumber && {
        pagesize: `${pageSize}`,
        recfirstpage: `${pageSize}`,
        sortBy: DEFAULT_REVIEWS_SORT_BY
      }),
      ...(!ppq && { startIndex })
    },
  };

  const { data, loading, error, variables } = useDataModel('product', productOptions);
  const { data: reviewData, loading: reviewLoading, error: reviewError } = useDataModel('reviews', reviewsOptions);

  const props = {
    ppq,
    pageNumber,
  };

  const helmetData = {
    product: data?.product,
    reviews: reviewData?.reviews
  };
  const deps = data?.product && reviewData?.reviews && data?.product?.itemId;
  useHelmet('pprMetadata', { data: helmetData, props, variables, skipCleanup: true }, pprMetaDataHandler, [deps]);
  return null;

};

PprMetadata.displayName = 'PprMetadata';

PprMetadata.dataModel = {
  product: params({ itemId: stringType().isRequired() }).shape({
    itemId: stringType(),
    dataSources: stringType(),
    availabilityType: shapeType({
      discontinued: boolType(),
      status: boolType()
    }),
    details: shapeType({
      description: stringType()
    }),
    identifiers: shapeType({
      brandName: stringType(),
      canonicalUrl: stringType(),
      itemId: stringType(),
      modelNumber: stringType(),
      productLabel: stringType(),
      storeSkuNumber: stringType(),
      upcGtin13: stringType()
    }),
    media: shapeType({
      image: shapeType({
        url: stringType()
      }).client(),
      images: arrayType(shapeType({
        url: stringType(),
        type: stringType(),
        subType: stringType(),
        sizes: arrayType(stringType())
      })),
      video: arrayType(shapeType({
        shortDescription: stringType(),
        thumbnail: stringType(),
        url: stringType(),
        videoStill: stringType(),
        uploadDate: stringType(),
        dateModified: stringType(),
      }))
    }),
    pricing: params({ storeId: stringType() }).shape({
      promotion: shapeType({
        dates: shapeType({
          end: stringType()
        })
      }),
      value: numberType({ float: true }),
      original: numberType({ float: true })
    }),
    reviews: shapeType({
      ratingsReviews: shapeType({
        averageRating: stringType(),
        totalReviews: stringType()
      })
    }),
    seo: shapeType({
      seoKeywords: stringType(),
      seoDescription: stringType()
    }),
    specificationGroup: arrayType(shapeType({
      specifications: arrayType(shapeType({
        specName: stringType(),
        specValue: stringType()
      })),
      specTitle: stringType()
    })),
    taxonomy: shapeType({
      brandLinkUrl: stringType(),
      breadCrumbs: arrayType(shapeType({
        label: stringType(),
        url: stringType()
      }))
    })
  }),
  reviews: params({ itemId: stringType().isRequired() }).shape({
    Results: arrayType(shapeType({
      Rating: numberType(),
      ReviewText: stringType(),
      Title: stringType(),
      UserNickname: stringType()
    })),
    Includes: shapeType({
      Products: shapeType({
        store: shapeType({
          FilteredReviewStatistics: shapeType({
            AverageOverallRating: stringType()
          })
        })
      })
    }),
    TotalResults: numberType()
  })
};

PprMetadata.propTypes = {
  itemId: string.isRequired,
  page: string,
  ppq: bool,
};

PprMetadata.defaultProps = {};

export { PprMetadata };
